import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { useCachedData } from '../hooks/useCachedData';
import { BarChart3, AlertTriangle, RefreshCw } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

import { DATE_PRESETS, loadSavedViews, persistSavedViews } from './period-analysis/constants';
import { AnalysisSkeleton } from './period-analysis/AnalysisSkeleton';
import { SavedViewsControls } from './period-analysis/SavedViewsControls';
import { GranularityToggles, DateRangePresets } from './period-analysis/RangeToggles';
import { SummaryStatsRow } from './period-analysis/SummaryStatsRow';
import { TrendChart } from './period-analysis/TrendChart';
import { PatternDetectionGrid } from './period-analysis/PatternDetectionGrid';
import { AIComparisonsSection } from './period-analysis/AIComparisonsSection';
import { ComparisonDetailModal } from './period-analysis/ComparisonDetailModal';
import { DrilldownModal } from './period-analysis/DrilldownModal';

const PeriodAnalysis = () => {
  const { API } = useContext(AppContext);
  const { filters, setFilters } = useFilters();
  const navigate = useNavigate();

  // State
  const [granularity, setGranularity] = useState('weekly');
  const [datePreset, setDatePreset] = useState('last3months');
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [aiComparisons, setAiComparisons] = useState([]);
  const [comparisonsLoading, setComparisonsLoading] = useState(true);
  const [selectedMetrics, setSelectedMetrics] = useState(['premium_hours', 'total_hours']);
  const [showRollingAvg, setShowRollingAvg] = useState(true);
  const [error] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [patternsLoading, setPatternsLoading] = useState(true);
  const [selectedComparison, setSelectedComparison] = useState(null);

  // Drill-down state
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // Saved Views state
  const [savedViews, setSavedViews] = useState(loadSavedViews);
  const [showSavedViews, setShowSavedViews] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const rollingWindow = useMemo(() => {
    switch (granularity) {
      case 'daily': return 7;
      case 'weekly': return 4;
      case 'biweekly': return 3;
      case 'monthly': return 3;
      case 'quarterly': return 2;
      default: return 4;
    }
  }, [granularity]);

  const filteredData = useMemo(() => {
    if (!timeSeriesData.length) return [];

    let data = [...timeSeriesData];

    if (datePreset !== 'all') {
      const preset = DATE_PRESETS.find(p => p.id === datePreset);
      if (preset) {
        if (preset.id === 'ytd') {
          const currentYear = new Date().getFullYear();
          data = data.filter(d => new Date(d.period_start).getFullYear() === currentYear);
        } else if (preset.weeks) {
          let periodsToShow = preset.weeks;
          if (granularity === 'daily') periodsToShow = preset.weeks * 7;
          else if (granularity === 'biweekly') periodsToShow = Math.ceil(preset.weeks / 2);
          else if (granularity === 'monthly') periodsToShow = Math.ceil(preset.weeks / 4);
          else if (granularity === 'quarterly') periodsToShow = Math.ceil(preset.weeks / 13);
          data = data.slice(-periodsToShow);
        }
      }
    }

    return data.map((item, index) => {
      let rollingSum = 0;
      let rollingCount = 0;
      for (let i = Math.max(0, index - rollingWindow + 1); i <= index; i++) {
        if (data[i]?.premium_percent !== undefined) {
          rollingSum += data[i].premium_percent;
          rollingCount++;
        }
      }
      return {
        ...item,
        rolling_avg: rollingCount > 0 ? Math.round((rollingSum / rollingCount) * 10) / 10 : null
      };
    });
  }, [timeSeriesData, datePreset, granularity, rollingWindow]);

  const fetchAllData = useCallback(async () => {
    const filterParams = {
      start_date: filters?.start_date || undefined,
      end_date: filters?.end_date || undefined,
      location: filters?.location || undefined,
      department: filters?.department || undefined,
      manager: filters?.manager || undefined,
      pay_types: filters?.pay_types && filters.pay_types.length > 0 ? filters.pay_types : undefined
    };

    const [tsRes, compRes, patRes] = await Promise.all([
      axios.post(`${API}/analytics/time-series`, { granularity, ...filterParams }).catch(() => ({ data: { data: [] } })),
      axios.post(`${API}/analytics/ai-comparisons`, { ...filterParams, min_employee_count: 3, min_variance: 3.0 }).catch(() => ({ data: { comparisons: [] } })),
      axios.post(`${API}/analytics/patterns`, filterParams).catch(() => ({ data: { patterns: null } }))
    ]);

    return {
      timeSeries: tsRes.data.data || [],
      comparisons: compRes.data.comparisons || [],
      patterns: patRes.data.patterns || null
    };
  }, [API, granularity, filters?.start_date, filters?.end_date, filters?.location, filters?.department, filters?.manager, filters?.pay_types]);

  const { data: cachedTrendData, loading } = useCachedData(
    'trend_analysis',
    fetchAllData,
    [granularity, filters?.start_date, filters?.end_date, filters?.location, filters?.department, filters?.manager, JSON.stringify(filters?.pay_types)]
  );

  useEffect(() => {
    if (cachedTrendData) {
      setTimeSeriesData(cachedTrendData.timeSeries);
      setAiComparisons(cachedTrendData.comparisons);
      setPatterns(cachedTrendData.patterns);
      setComparisonsLoading(false);
      setPatternsLoading(false);
    }
  }, [cachedTrendData]);

  const toggleMetric = (metric) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metric)) {
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== metric);
      }
      return [...prev, metric];
    });
  };

  const handleViewInDashboard = (comparison) => {
    setFilters(prev => ({ ...prev, department: comparison.entity_a.department }));
    navigate('/dashboard?tab=supervisors');
    toast.success(`Filtered to ${comparison.entity_a.department} department`);
  };

  const handleDataPointClick = useCallback(async (data) => {
    const point = data?.payload;
    if (!point?.period_start || !point?.period_end) return;

    setDrilldownLoading(true);
    try {
      const response = await axios.post(`${API}/analytics/spike-drilldown`, {
        period_start: point.period_start,
        period_end: point.period_end,
        location: filters?.location || undefined,
        department: filters?.department || undefined
      });
      setDrilldownData(response.data);
    } catch {
      toast.error('Failed to load drill-down data');
    } finally {
      setDrilldownLoading(false);
    }
  }, [API, filters?.location, filters?.department]);

  const handleSaveView = () => {
    if (!saveViewName.trim()) return;
    const newView = {
      id: Date.now().toString(),
      name: saveViewName.trim(),
      config: { granularity, datePreset, selectedMetrics, showRollingAvg },
      createdAt: new Date().toISOString()
    };
    const updated = [newView, ...savedViews];
    setSavedViews(updated);
    persistSavedViews(updated);
    setSaveViewName('');
    setShowSaveInput(false);
    toast.success(`View "${newView.name}" saved`);
  };

  const handleLoadView = (view) => {
    setGranularity(view.config.granularity);
    setDatePreset(view.config.datePreset);
    setSelectedMetrics(view.config.selectedMetrics);
    setShowRollingAvg(view.config.showRollingAvg);
    setShowSavedViews(false);
    toast.success(`Loaded view "${view.name}"`);
  };

  const handleDeleteView = (viewId) => {
    const updated = savedViews.filter(v => v.id !== viewId);
    setSavedViews(updated);
    persistSavedViews(updated);
    toast.success('View deleted');
  };

  const summaryStats = useMemo(() => {
    if (!filteredData.length) return null;
    const totalHours = filteredData.reduce((sum, d) => sum + (d.total_hours || 0), 0);
    const premiumHours = filteredData.reduce((sum, d) => sum + (d.premium_hours || 0), 0);
    const avgPremiumPercent = totalHours > 0 ? (premiumHours / totalHours) * 100 : 0;
    const firstPeriod = filteredData[0];
    const lastPeriod = filteredData[filteredData.length - 1];
    const trend = firstPeriod?.premium_percent && lastPeriod?.premium_percent
      ? lastPeriod.premium_percent - firstPeriod.premium_percent
      : 0;
    return { totalHours, premiumHours, avgPremiumPercent, trend, periodCount: filteredData.length };
  }, [filteredData]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="period-analysis-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <BarChart3 className="h-8 w-8" />
            Trend Analysis
          </h1>
          <p className="text-muted-foreground mt-1">
            Analyze overtime trends over time — click any bar to drill down
          </p>
        </div>

        <SavedViewsControls
          savedViews={savedViews}
          showSaveInput={showSaveInput}
          setShowSaveInput={setShowSaveInput}
          showSavedViews={showSavedViews}
          setShowSavedViews={setShowSavedViews}
          saveViewName={saveViewName}
          setSaveViewName={setSaveViewName}
          granularity={granularity}
          datePreset={datePreset}
          selectedMetrics={selectedMetrics}
          onSaveView={handleSaveView}
          onLoadView={handleLoadView}
          onDeleteView={handleDeleteView}
        />
      </div>

      <GranularityToggles value={granularity} onChange={setGranularity} />
      <DateRangePresets
        value={datePreset}
        onChange={setDatePreset}
        granularity={granularity}
        periodCount={filteredData.length}
      />

      {loading ? (
        <AnalysisSkeleton />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700">{error}</p>
          <Button onClick={() => window.location.reload()} variant="outline" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      ) : (
        <>
          <SummaryStatsRow stats={summaryStats} />

          <TrendChart
            filteredData={filteredData}
            granularity={granularity}
            datePreset={datePreset}
            selectedMetrics={selectedMetrics}
            toggleMetric={toggleMetric}
            showRollingAvg={showRollingAvg}
            setShowRollingAvg={setShowRollingAvg}
            rollingWindow={rollingWindow}
            drilldownLoading={drilldownLoading}
            onDataPointClick={handleDataPointClick}
          />

          <PatternDetectionGrid patterns={patterns} loading={patternsLoading} />

          <AIComparisonsSection
            comparisons={aiComparisons}
            loading={comparisonsLoading}
            onExplore={setSelectedComparison}
          />
        </>
      )}

      <ComparisonDetailModal
        comparison={selectedComparison}
        onClose={() => setSelectedComparison(null)}
        onViewInDashboard={handleViewInDashboard}
      />

      <AnimatePresence>
        {drilldownData && (
          <DrilldownModal data={drilldownData} onClose={() => setDrilldownData(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PeriodAnalysis;
